import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Questions.css";
import { createSubmittedQuestion } from "../../../../api/questions";
import { scrollToElementById } from "../../../../utils/scrollToId";

const QUESTION_TEXT_MAX = 8000;

const Questions = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    location: "",
    questionText: "",
  });

  useEffect(() => {
    if (location.hash !== "#questions-contact-form") return;
    const frame = window.requestAnimationFrame(() => {
      scrollToElementById("questions-contact-form");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!String(form.name || "").trim()) {
      setError(t("contactForm.errors.nameRequired"));
      return;
    }
    if (!String(form.email || "").trim()) {
      setError(t("contactForm.errors.emailRequired"));
      return;
    }
    if (!String(form.phoneNumber || "").trim()) {
      setError(t("contactForm.errors.phoneRequired"));
      return;
    }
    if (!String(form.location || "").trim()) {
      setError(t("contactForm.errors.locationRequired"));
      return;
    }
    const questionText = String(form.questionText || "").trim();
    if (!questionText) {
      setError(t("contactForm.errors.questionRequired"));
      return;
    }
    if (questionText.length > QUESTION_TEXT_MAX) {
      setError(t("contactForm.errors.questionMaxLength", { max: QUESTION_TEXT_MAX }));
      return;
    }

    setSubmitting(true);
    try {
      await createSubmittedQuestion({
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim(),
        phoneNumber: String(form.phoneNumber || "").trim(),
        location: String(form.location || "").trim(),
        questionText,
      });
      setSuccess(t("contactForm.success"));
      setForm({
        name: "",
        email: "",
        phoneNumber: "",
        location: "",
        questionText: "",
      });
    } catch (submitError) {
      setError(
        submitError?.response?.data?.message ||
          submitError?.message ||
          t("contactForm.errors.submitFailed")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const questionLen = String(form.questionText || "").length;

  return (
    <section id="questions-contact-form" className="questionsContact" aria-label="Questions">
      <div className="questionsContact-inner">
        <div className="questionsContact-left">
          <span className="questionsContact-tag">{t("sections.academy.questions.tag")}</span>

          <h2 className="questionsContact-title">
            {t("sections.academy.questions.title")}{" "}
            <span>{t("sections.academy.questions.titleAccent")}</span>
          </h2>

          <p className="questionsContact-text">{t("sections.academy.questions.text")}</p>
        </div>

        <div className="questionsContact-right">
          <h3 className="questionsContact-formTitle">{t("sections.academy.questions.formTitle")}</h3>

          <form className="questionsContact-form" onSubmit={handleSubmit} noValidate>
            <div className="questionsContact-row questionsContact-rowHalf">
              <input
                type="text"
                placeholder={t("contactForm.namePlaceholder")}
                aria-label={t("contactForm.nameAriaLabel")}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                autoComplete="name"
              />
              <input
                type="email"
                placeholder={t("contactForm.emailPlaceholder")}
                aria-label={t("contactForm.emailAriaLabel")}
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                autoComplete="email"
              />
            </div>

            <div className="questionsContact-row">
              <input
                type="tel"
                placeholder={t("contactForm.phonePlaceholder")}
                aria-label={t("contactForm.phoneAriaLabel")}
                value={form.phoneNumber}
                onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                autoComplete="tel"
              />
            </div>

            <div className="questionsContact-row">
              <input
                type="text"
                placeholder={t("contactForm.locationPlaceholder")}
                aria-label={t("contactForm.locationAriaLabel")}
                value={form.location}
                onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                autoComplete="address-level2"
              />
            </div>

            <div className="questionsContact-row questionsContact-rowTextarea">
              <textarea
                id="questions-question-text"
                placeholder={t("contactForm.questionPlaceholder")}
                aria-label={t("contactForm.questionAriaLabel")}
                value={form.questionText}
                maxLength={QUESTION_TEXT_MAX}
                onChange={(e) => setForm((p) => ({ ...p, questionText: e.target.value }))}
                rows={6}
              />
              <span className="questionsContact-charCount" aria-live="polite">
                {questionLen} / {QUESTION_TEXT_MAX}
              </span>
            </div>

            {error ? <p className="questionsContact-message questionsContact-messageError">{error}</p> : null}
            {success ? <p className="questionsContact-message questionsContact-messageSuccess">{success}</p> : null}

            <div className="questionsContact-action">
              <button type="submit" className="questionsContact-submit" disabled={submitting}>
                {submitting ? t("contactForm.submitting") : t("contactForm.submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Questions;
